import ClientFormConfig from "./form";
import { ClientFormData, ClientRelatedAttributes } from "@/formTypes/ClientFormDataType";
import GenericForm from "../GenericForm";
import { ClientApi, ClientDataType } from "@/api/ClientApi";
import { useEffect, useMemo, useRef, useState } from "react";
import { PriceApi } from "@/api/PricesApi";
import { useAppSelector } from "@/store/hooks";
import SearchClientByIdentificationOffCanvas from "@components/layout/Offcanvas/SearchClientByIdentificationOffCanvas";
import { DynamicFormRef } from "@mavichpe/dynamic-forms";
import { ClientShippingOption, readClientShippingOptions, serializeClientShippingOptions } from "@utils/ClientShipping";
import { ShippingProviderApi, ShippingProviderDataType } from "@/api/ShippingProviderApi";
import ClientShippingOptionsEditor from "../ClientShippingOptionsEditor";

type ClientFormProps = {
  id?: string;
  formId: string,
  saveRedirectUrl?: string,
  onStored?: (clientData?: ClientDataType) => void
}

const ClientForm = (props: ClientFormProps) => {
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [shippingProviders, setShippingProviders] = useState<ShippingProviderDataType[]>([]);
  /**
   * Las opciones de envio viven fuera del estado de DynamicForm porque son una lista, no un
   * campo: el editor las gestiona y `createFn`/`updateFn` las serializan a `shipping_data`.
   */
  const [shippingOptions, setShippingOptions] = useState<ClientShippingOption[]>([]);
  const suscriptionData = useAppSelector(state => state.suscription.suscription || {})
  const currencySymbol = useAppSelector(state => state.currency.currencySymbol) || "";
  const formRef = useRef<DynamicFormRef>(null);

  useEffect(() => {
    const loadPrices = async () => {
      const priceOptions = await PriceApi.optionsList()
      setPrices(priceOptions)
    }
    loadPrices()
  }, [suscriptionData.id])

  useEffect(() => {
    const loadShippingProviders = async () => {
      const { items = [] } = await ShippingProviderApi.fetchAll()
      setShippingProviders(items.filter(provider => provider.active !== false))
    }
    loadShippingProviders()
  }, [suscriptionData.id])

  /**
   * Las opciones del select "priceId" (Tipo de Precio) se cargan de forma asincrona
   * (Precio 1..4 cuando el manejo es por 4 precios, o la lista de precios del usuario
   * cuando es por formula). Debemos generar una NUEVA referencia de configuracion cada
   * vez que cambian las opciones para que GenericForm/DynamicForm reconstruyan la
   * estructura; mutar el singleton ClientFormConfig en su lugar no dispara el re-render.
   */
  const formConfig = useMemo(() => ({
    ...ClientFormConfig,
    sections: ClientFormConfig.sections.map(section => ({
      ...section,
      fields: section.fields.map(row =>
        row.map(field => {
          if (field.name === "priceId") {
            return { ...field, options: prices }
          }
          if (field.name === "shipping_data") {
            // El CUSTOM_ELEMENT nace con `element: null` en la config estatica: el editor
            // necesita el estado de este componente, asi que se inyecta aqui.
            return {
              ...field,
              element: (
                <ClientShippingOptionsEditor
                  value={shippingOptions}
                  onChange={setShippingOptions}
                  providers={shippingProviders}
                  currencySymbol={currencySymbol}
                />
              ),
            }
          }
          return field
        })
      ),
    })),
  }), [prices, shippingOptions, shippingProviders, currencySymbol])

  /** Siembra el editor con lo que trae el cliente al abrir la ficha. */
  const loadClient = async (id: string): Promise<ClientDataType> => {
    const client = await ClientApi.get(id);
    setShippingOptions(readClientShippingOptions(client || {}));
    return client;
  }

  /**
   * Las opciones viven en el estado del editor, no en el de DynamicForm, asi que se
   * serializan aqui. El campo del form esta marcado `exludeFromSubmit`, por eso lo que
   * llegue en `formData.shipping_data` se descarta.
   */
  const withShippingOptions = (formData: ClientDataType): ClientDataType => ({
    ...formData,
    shipping_data: serializeClientShippingOptions(shippingOptions),
  })

  const onClientSelected = (clientData?: ClientDataType) => {
    formRef.current?.setFormValue("multiple", clientData, "multipleData")
  }

  return (
    <>
      <GenericForm<ClientFormData, ClientDataType>
        id={props.id}
        ref={formRef}
        formId={props.formId}
        onStored={props.onStored}
        saveRedirectUrl={props.saveRedirectUrl}
        formConfig={formConfig}
        getNextCode={() => ClientApi.getNextCode("codigo")}
        modelRelativeObjects={ClientRelatedAttributes}
        getById={(id) => loadClient(id)}
        createFn={(data) => ClientApi.create(withShippingOptions(data))}
        updateFn={(id, data) => ClientApi.update(id, withShippingOptions(data))}
        successMessage="El client ha sido almacenada correctamente"
        errorMessage="No fue posible almacenar la información del client"
      />
      <SearchClientByIdentificationOffCanvas
        onClientSelected={onClientSelected}
      />
    </>
  )
}

export default ClientForm;